import React, { useState, useEffect } from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { popIn } from '../../shared/component/Animation';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import deleteIcon from '../../asset/icon/delete.png';
import OptimizedImg from '../../shared/component/OptimizedImg';
import { DisplayAlignment, DisplaySize, ProjectElementType } from '../../shared/enum/EnumRepository';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { useBulk } from '../../shared/hooks/useApi/useBulk';
import { UpdateImgReq, UpdatePEEachInBulkReq } from '../../shared/dto/ReqDtoRepository';
import { useBulkStore } from '../../shared/store/WorkListStore';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useProjectElementActions, useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { getImageDimensions, removeFileExtension, removeFileExtensionWithinLimit } from '../../shared/util/imageUtils';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { paperTextureModal } from '../../shared/component/paperTexture';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { DOCUMENT_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';

const BulkCreateModal: React.FC = () => {
  const { user } = useAuth();
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { createWorkBulk, createDetailBulk, createPEBulk } = useBulk();
  const { updateWorkBulk } = useWorkListActions();
  const { updateWorkDetailBulk } = useWorkDetailActions();
  const { project } = useProjectDetailState();
  const { projectElementList } = useProjectElementState();
  const { updateProjectElementBulk } = useProjectElementActions();
  const { current, total } = useBulkStore();
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const activeWork = useWorkViewStore((s) => s.activeWork);

  // Create object URLs for file previews
  useEffect(() => {
    if (!standardModal || !standardModal.data) return;
      const urls: { [key: string]: string } = {};
      standardModal.data.forEach((file) => {
        urls[file.name] = URL.createObjectURL(file);
      });
      setFileUrls(urls);

      // Cleanup function to revoke object URLs
      return () => {
        Object.values(urls).forEach(url => URL.revokeObjectURL(url));
      };

  }, [standardModal]);

  if (standardModal == null || !user || !standardModal.data) return null;

  const showParseMessage = standardModal.value === 'list';

  const handleCreateAll = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!standardModal.data) return;
    const files = standardModal.data;

    // 이미지 크기를 병렬로 추출 (파일만 있으면 되므로 먼저 수행)
    const imageDimensions = await Promise.all(
      files.map(file => getImageDimensions(file))
    );

    // 여기서 하고 uploadFileWithPreSignedUrl 까지 끝내고
    if (standardModal.value === 'list') {
      const results = await createWorkBulk(files);
      if (!results) return;
      // 이후 bulk update 진행
      const workList = results.map((result, i) => ({
        id: result.workId,
        originUrl: result.originUrl,
        width: imageDimensions[i].width,
        height: imageDimensions[i].height,
        title: removeFileExtension(files[i].name)
      })) satisfies UpdateImgReq[];
      await updateWorkBulk({ workList });
    } else if (standardModal.value === 'project-document') {
      if (!project) return;
      const peFiles = files.map((file) => ({ file, peType: ProjectElementType.DOCUMENT }));
      const results = await createPEBulk(peFiles, project.id, projectElementList.length);
      if (!results) return;
      const peList = results.map((result, i) => ({
        peId: result.peId,
        projectElementType: ProjectElementType.DOCUMENT,
        originUrl: result.originUrl,
        width: imageDimensions[i].width,
        height: imageDimensions[i].height,
        description: removeFileExtensionWithinLimit(files[i].name, DOCUMENT_DESCRIPTION_MAX_LENGTH),
      })) satisfies UpdatePEEachInBulkReq[];
      await updateProjectElementBulk(project.id, { peList });
    } else {
      if (!activeWork) return;
      const results = await createDetailBulk(files, activeWork.id);
      if (!results) return;
      // 이후 bulk update 진행
      const detailList = results.map((result, i) => ({
        id: result.workDetailId,
        originUrl: result.originUrl,
        width: imageDimensions[i].width,
        height: imageDimensions[i].height,
        description: removeFileExtension(files[i].name)
      })) satisfies UpdateImgReq[];
      await updateWorkDetailBulk({ detailList });
    }
  }

  const handleClose = () => {
    clearModal();
  };

  return (
    <BulkCreateModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <HeaderRow>
        <Title>{getModalMessage('CREATE_BUIK', { title: standardModal.title || '' })}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={handleClose}
        />
      </HeaderRow>
      {showParseMessage && (
        <>
          <ParseMessage>{getModalMessage('CREATE_BULK_FILENAME_HINT')}</ParseMessage>
          <ParseMessage>{getModalMessage('CREATE_BULK_FILENAME_FORMAT')}</ParseMessage>
        </>
      )}
      <LoadingBarContainer>
          <LoadingBar>
            <LoadingBarFill $usagePercentage={(current / (total || 0)) * 100} />
          </LoadingBar>
          <LoadingText>
            {current} / {total}
          </LoadingText>
      </LoadingBarContainer>
      <BulkCreationContainer>
        {standardModal.data?.map((file) => (
          <EachImageLine key={file.name}>
            <ImgContainer>
            <ImgWrapper>
              <OptimizedImg
                imageUrl={fileUrls[file.name] || ''}
                alt={file.name}
                StyledImg={ThumbnailImg}
                displaySize={DisplaySize.REGULAR}
                displayAlignment={DisplayAlignment.CENTER}
                priority={false} />
            </ImgWrapper>
            </ImgContainer>
            <EachImageLineTitle>
              {file.name}
            </EachImageLineTitle>
          </EachImageLine>
        ))}
      </BulkCreationContainer>
      <HeadlessBtn
        value={"Create ALL"}
        handleClick={handleCreateAll}
        StyledBtn={BlackFullWidthBtn}
      />
    </BulkCreateModalFrame>
  );
}

export const BulkCreateModalFrame = styled.div<{ $isClosing: boolean }>`
  padding: 30px 25px;
  width: fit-content;
  min-height: 60vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'scale(0.8)' : 'scale(1)')};

  transition: all 0.3s ease-in;
  ${paperTextureModal}
  ${popIn}
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
`;

const Title = styled.div`
  min-width: 0;
  width: fit-content;
  border-bottom: 1.6px solid ${({ theme }) => theme.colors.color_Gray_02};

  padding: 12px 0px;
  padding-right: 5px;
  ${({ theme }) => theme.typography.Body_01_1};
`;

const BulkCreationContainer = styled.section`
  width: clamp(500px, 50vw, 600px);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-bottom: 20px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const EachImageLine = styled.div`
  width: 100%;
  height: clamp(52px, 10vh, 60px);
  display: flex;
  justify-content: space-between;
  padding: 10px 0px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const ImgContainer = styled.div`
  position: relative;
  height: fit-content;
  width: auto;
  min-width: 80px;

  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const ImgWrapper = styled.div`
  position: relative;
  height: 40px;
  width: auto;

  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const ThumbnailImg = styled.img<StyledImgProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const EachImageLineTitle = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const ParseMessage = styled.div`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const LoadingBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 30px;
`;

const LoadingBar = styled.div`
  width: 100%;
  height: 5px;
  margin: 10px 0px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
  border-radius: 1px;
  overflow: hidden;
  position: relative;
`;

const LoadingBarFill = styled.div<{ $usagePercentage: number }>`
  width: ${({ $usagePercentage }) => Math.min($usagePercentage, 100)}%;
  height: 100%;
  background-color: ${({ theme, $usagePercentage }) => {
    if ($usagePercentage >= 95) return theme.colors.color_Gray_01;
    if ($usagePercentage >= 85) return theme.colors.color_Gray_02;
    if ($usagePercentage >= 70) return theme.colors.color_Gray_03;
    if ($usagePercentage >= 50) return theme.colors.color_Gray_04;
    return theme.colors.color_Gray_05;
  }};
  transition: width 0.3s ease;
`;

const LoadingText = styled.div`
  width: 100%;
  text-align: right;
  ${({ theme }) => theme.typography.Body_03_2};
`;

export default BulkCreateModal;
